import { useEffect, useRef, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import AdUnit from './AdUnit';
import PartnerBannerCarousel from './PartnerBannerCarousel';
import { fetchPublicAds, getCachedPublicAds } from '../utils/publicAds.js';
import './Sidebar.css';

import { API_URL as API } from '../config/env.js';

export default function Sidebar() {
  const { t } = useLang();
  const cached = getCachedPublicAds();
  const [sidebarAds, setSidebarAds] = useState(() => cached?.sidebar || []);
  const [bannerAds, setBannerAds] = useState(() => cached?.banner || []);
  const retryRef = useRef(0);
  const timerRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    async function load(force = false) {
      try {
        const data = await fetchPublicAds(API, { force });
        if (cancelled) return;
        setSidebarAds(data.sidebar || []);
        setBannerAds(data.banner || []);
        retryRef.current = 0;
      } catch {
        if (cancelled) return;
        // Keep last-good ads visible; retry with backoff.
        const attempt = retryRef.current + 1;
        retryRef.current = attempt;
        if (attempt <= 4) {
          const delay = Math.min(1500 * 2 ** (attempt - 1), 12000);
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => load(true), delay);
        }
      }
    }

    load(false);

    const onVisible = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    const onFocus = () => load(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const hasSidebar = sidebarAds.length > 0;
  const hasBanner = bannerAds.length > 0;
  if (!hasSidebar && !hasBanner) return null;

  return (
    <aside className="sidebar">
      <div className="sidebar-card sidebar-card--ads">
        <div className="sidebar-title">{t.adsTitle}</div>

        {hasSidebar && (
          <div className="sidebar-ads-stack">
            {sidebarAds.map((ad) => (
              <AdUnit key={ad.id} ad={ad} variant="sidebar" />
            ))}
          </div>
        )}

        {hasBanner && (
          <div className="sidebar-partner-ads">
            <div className="sidebar-partner-ads__label">
              <span>{t.adPartnerStrip || 'Our partners'}</span>
              <em>{t.adSponsored || 'Sponsored'}</em>
            </div>
            <PartnerBannerCarousel
              banners={bannerAds}
              ariaLabel={t.adPartnerStrip || 'Our partners'}
            />
          </div>
        )}
      </div>
    </aside>
  );
}
